
import { Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { IOverviewMessage } from '@/utils/types'
import { TableUtils, ITableColumn } from '@/utils/table'
import { sumBy, get } from 'lodash'
import { ACTION_APP } from '@/store/actions'
import { DatetimeUtils } from '@/utils/datetime'

export enum ColumnName {
  DATE = 'date',
  DELIVERED = 'delivered',
  GTR = 'gtr',
  CVR = 'cvr',
  TARGET = 'target',
  GOAL = 'total_user_of_goal',
  CLICKS = 'total_click',
  DISPLAYS = 'total_display',
}

export default class TableMixin extends Vue {
  @Prop({ type: String, required: true })
  title!: string

  @Prop()
  description!: string

  @Prop({ type: Array, default: () => [] })
  rows!: IOverviewMessage[]

  @Prop()
  isRichMenu!: boolean

  @Prop({ type: String, required: false })
  appId!: string

  @Prop({ type: Object, required: false })
  dateRange!: {
    startDate: string
    endDate: string
  }

  @Prop({ type: String, required: false })
  messageType!: string

  protected tableData: IOverviewMessage[] = []

  get messageTypeForAPI(): string {
    return this.messageType || this.getDefaultMessageType()
  }

  protected getDefaultMessageType(): string {
    return ''
  }

  @Watch('dateRange', { deep: true, immediate: true })
  async onPropsChange() {
    if (this.appId && this.dateRange && this.messageTypeForAPI) {
      await this.fetchData()
    }
  }

  async fetchData() {
    try {
      const resp = await this.$store.dispatch(ACTION_APP.MESSAGE_STATISTICS, {
        app_id: this.appId,
        date_from: DatetimeUtils.formatDate(this.dateRange.startDate),
        date_to: DatetimeUtils.formatDate(this.dateRange.endDate),
        type: this.messageTypeForAPI,
      })

      this.tableData = get(resp, 'message_statistic', [])
    } catch (error) {
      console.error('Error fetching message statistics:', error)
      this.tableData = []
    }
  }

  get effectiveRows(): IOverviewMessage[] {
    return this.rows.length > 0 ? this.rows : this.tableData
  }

  protected get baseColumns(): ITableColumn[] {
    return [
      {
        name: 'date',
        label: '',
        field: 'date',
        required: true,
        align: 'left',
      },
    ]
  }

  protected get commonGoalColumns(): ITableColumn[] {
    return [
      {
        name: ColumnName.CLICKS,
        label: this.$t('label.total_click'),
        field: 'total_click',
        align: 'right',
        format: TableUtils.formatNumber,
      },
      {
        name: ColumnName.GTR,
        label: 'GTR',
        field: (row: any) => TableUtils.calculatePercent(row.total_user_of_goal, row.delivered),
        align: 'right',
        format: TableUtils.formatPercent,
      },
      {
        name: ColumnName.GOAL,
        label: this.$t('label.total_user_of_goal'),
        field: 'total_user_of_goal',
        align: 'right',
        format: TableUtils.formatNumber,
      },
      {
        name: ColumnName.CVR,
        label: 'CVR',
        field: (row: any) => TableUtils.calculatePercent(row.success_count, row.total_click),
        align: 'right',
        format: TableUtils.formatPercent,
      },
      {
        name: ColumnName.TARGET,
        label: this.$t('label.success_count'),
        field: 'success_count',
        align: 'right',
        format: TableUtils.formatNumber,
      },
    ]
  }

  get columns(): ITableColumn[] {
    return this.commonGoalColumns
  }

  protected getCommonColumns(columnNames: ColumnName[]): ITableColumn[] {
    return this.commonGoalColumns.filter((column) => columnNames.includes(column.name as ColumnName))
  }

  protected calculateSummary(): Partial<Record<ColumnName, number>> {
    const summary: Partial<Record<ColumnName, number>> = {
      [ColumnName.DELIVERED]: 0,
      [ColumnName.GTR]: 0,
      [ColumnName.CVR]: 0,
      [ColumnName.TARGET]: 0,
      [ColumnName.GOAL]: 0,
      [ColumnName.CLICKS]: 0,
    }

    const totals = {
      delivered: sumBy(this.effectiveRows, 'delivered'),
      total_user_of_goal: sumBy(this.effectiveRows, 'total_user_of_goal'),
      total_click: sumBy(this.effectiveRows, 'total_click'),
      success_count: sumBy(this.effectiveRows, 'success_count'),
    }

    this.columns.forEach((col) => {
      if (col.name === ColumnName.DATE) return

      switch (col.name) {
        case ColumnName.GTR:
          summary[col.name] = TableUtils.calculatePercent(totals.total_user_of_goal, totals.delivered)
          break
        case ColumnName.CVR:
          summary[col.name] = TableUtils.calculatePercent(totals.success_count, totals.total_click)
          break
        default: {
          const totalValue = totals[col.field as keyof typeof totals]
          if (totalValue !== undefined) {
            summary[col.name] = totalValue
          } else {
            summary[col.name] = sumBy(this.effectiveRows, col.field)
          }
          break
        }
      }
    })

    return summary
  }

  get summaryRow(): Partial<Record<ColumnName, number>> {
    const summary = this.calculateSummary()
    return summary
  }
}
